import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArchiveRestore, Layers, ThumbsUp, Trash2, Undo2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import CommentPanel from '../components/CommentPanel';
import ReviewStage from '../components/ReviewStage';
import ShareLinkManager from '../components/ShareLinkManager';
import TranscriptDownloads from '../components/TranscriptDownloads';
import Badge from '../ui/Badge';
import BreadcrumbBar from '../ui/BreadcrumbBar';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import Menu from '../ui/Menu';
import Popover from '../ui/Popover';
import Skeleton from '../ui/Skeleton';
import Stage from '../ui/Stage';
import Timecode from '../ui/Timecode';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { TierToggle } from '../shell/ReviewShell';
import { clean } from '../shell/copy';

// Review page (/review/:videoAssetId), signed-in staff and client accounts: the Review set (DESIGN rev 2.1 §2.6, §3.3,
// docs/design-gate/v1/share-A.html). The shell gives the icon rail and the review tier; this page draws the frame:
// breadcrumb bar (back · client / production / version · versions · Qual actions · Share · tier · comments) above a
// full-bleed stage with the Transport, and the 400 px comments column with the composer pinned (no name / email fields:
// the signed-in identity is a line in the composer). Details tab: file facts and the transcript with its downloads.
// Calls, unchanged:
//   GET    /api/video-assets/:id                         the asset (first; an external account on another client's
//                                                        asset gets its one 403 here and nothing else is called)
//   GET    /api/comments/:id                             comments (after the asset loads)
//   GET    /api/transcriptions/video_asset/:id           transcript (non-blocking)
//   GET    /api/video-assets/archived?liveProductionId|aiProductionId=   version list (non-blocking)
//   PATCH  /api/live-production/:id/status | /api/ai-production/:id/status  { status, changedBy }  (Qual context)
//   POST   /api/video-assets/:id/restore                 archived version back to current (Admin, Power User, Producer)
//   DELETE /api/video-assets/:id/permanent               archived version deleted from storage (Admin)
//   plus CommentPanel's comment calls and ShareLinkManager's share-link calls (Share opens it).

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtSize(bytes) {
  const n = Number(bytes);
  if (!n) return null;
  const u = ['B', 'KB', 'MB', 'GB']; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(i ? 1 : 0)} ${u[i]}`;
}

const QUAL_LABEL = 'Video Qual Review';

// Transcript: status, the four downloads, and the words (each timed word seeks the player on click).
function TranscriptSection({ transcription, baseName, onSeek }) {
  const words = useMemo(() => (transcription.transcriptJson?.transcripts?.[0]
    ? null
    : transcription.transcriptJson?.items?.filter(i => i.type === 'pronunciation')), [transcription]);
  const done = transcription.status === 'complete';
  return (
    <section className="ui-section" aria-label="Transcript">
      <div className="ui-section__head ui-section__head--center">
        <h2 className="ui-section__title">Transcript</h2>
        {!done && <Badge tone={transcription.status === 'failed' ? 'danger' : 'info'}>{transcription.status === 'failed' ? 'Failed' : 'In progress'}</Badge>}
      </div>
      {done && transcription.transcriptText && (
        <>
          <TranscriptDownloads transcriptionId={transcription.id} transcriptText={transcription.transcriptText} baseName={baseName} />
          {words?.length ? (
            // One listener for every word; a word with a start time carries it in data-t.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
            <p className="ui-p ui-words" onClick={e => { const t = e.target.closest('[data-t]')?.dataset.t; if (t) onSeek(parseFloat(t)); }}>
              {words.map((item, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={idx} data-t={item.start_time || undefined}>{item.alternatives[0]?.content || ''}{' '}</span>
              ))}
            </p>
          ) : <p className="ui-p ui-prewrap">{transcription.transcriptText}</p>}
        </>
      )}
      {!done && <p className="ui-p ui-text-2">{transcription.status === 'failed' ? 'The transcript could not be made for this video.' : 'The transcript is being made. Reload the page in a few minutes to see it.'}</p>}
    </section>
  );
}

export default function VideoReviewPage() {
  const { videoAssetId }    = useParams();
  const navigate            = useNavigate();
  const location            = useLocation();
  const [searchParams]      = useSearchParams();
  const { user }            = useAuth();
  const toast               = useToast();
  const confirm             = useConfirm();
  const initialCommentId    = searchParams.get('comment');
  const seekRef             = useRef(null);
  const initialSeekDone     = useRef(false);
  const playerApi           = useRef(null);

  const [videoAsset,           setVideoAsset]           = useState(null);
  const [comments,             setComments]             = useState([]);
  const [currentTimeSeconds,   setCurrentTimeSeconds]   = useState(0);
  const [activeCommentId,      setActiveCommentId]      = useState(initialCommentId || null);
  const [playerReady,          setPlayerReady]          = useState(false);
  const [loading,              setLoading]              = useState(true);
  const [error,                setError]                = useState(null);   // null | 'denied' | 'missing' | 'failed'
  const [archivedVersions,     setArchivedVersions]     = useState([]);
  const [transcription,        setTranscription]        = useState(null);
  const [versionsOpen,         setVersionsOpen]         = useState(false);
  const [shareOpen,            setShareOpen]            = useState(false);
  const [commentsHidden,       setCommentsHidden]       = useState(false);
  const [reload,               setReload]               = useState(0);

  const isAdmin = user?.roles?.some(r => ['Admin', 'Power User', 'Producer'].includes(r));
  // R-2 (Context.HeliconflowReviewUploadRestore.md): storage delete is an explicit Admin action.
  const canPermanentDelete = user?.roles?.includes('Admin');
  const canShare = !user?.isExternal && user?.roles?.some(r => ['Admin', 'Power User', 'Editor', 'Post Lead', 'Post_Lead'].includes(r));
  // Staff read frame-accurate timecode (Workbench, §3.2); client accounts read m:ss.s (Portal, §3.3).
  const tcFormat = user?.isExternal ? 'mss' : 'smpte';

  const fetchComments = useCallback(async () => {
    try {
      const res = await api.get(`/api/comments/${videoAssetId}`);
      setComments(res.data);
    } catch {
      // non-fatal
    }
  }, [videoAssetId]);

  useEffect(() => {
    // Reset all per-video state before loading the new one
    setVideoAsset(null);
    setComments([]);
    setArchivedVersions([]);
    setTranscription(null);
    setError(null);
    setLoading(true);
    setPlayerReady(false);
    setActiveCommentId(initialCommentId || null);
    initialSeekDone.current = false;
    let gone = false;

    async function load() {
      let asset;
      try {
        asset = (await api.get(`/api/video-assets/${videoAssetId}`)).data;
      } catch (err) {
        if (gone) return;
        const st = err?.response?.status;
        setError(st === 403 ? 'denied' : st === 404 ? 'missing' : 'failed');
        setLoading(false);
        return;
      }
      if (gone) return;
      // The asset first, then its comments: a 403 on the asset stops here, so the page makes one refused call, not two.
      await fetchComments();
      if (gone) return;
      setVideoAsset(asset);
      setLoading(false);

      // Load transcript in parallel, non-blocking
      api.get(`/api/transcriptions/video_asset/${videoAssetId}`)
        .then(r => { if (!gone) setTranscription(r.data); })
        .catch(() => {});

      // Fetch archived history for this production
      const prodParam = asset.liveProductionId
        ? `liveProductionId=${asset.liveProductionId}`
        : asset.aiProductionId
          ? `aiProductionId=${asset.aiProductionId}`
          : null;
      if (prodParam) {
        api.get(`/api/video-assets/archived?${prodParam}`)
          .then(r => { if (!gone) setArchivedVersions(r.data); })
          .catch(() => {});
      }
    }
    load();
    return () => { gone = true; };
    // initialCommentId is read once per video, as before.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoAssetId, fetchComments, reload]);

  // Deep-link: seek to the linked comment once player is ready and comments are loaded
  useEffect(() => {
    if (!initialCommentId || initialSeekDone.current || !playerReady || !comments.length) return;
    const target = comments.find(c => c.id === initialCommentId);
    if (target) {
      seekRef.current?.(Number(target.timestampSeconds));
      initialSeekDone.current = true;
    }
  }, [initialCommentId, playerReady, comments]);

  function handleSeekTo(secs, commentId) {
    seekRef.current?.(secs);
    if (commentId) setActiveCommentId(commentId);
  }

  function handleBack() {
    if (location.state?.returnTo) {
      const fwdState = location.state.returnVideo ? { openVideo: location.state.returnVideo } : undefined;
      navigate(location.state.returnTo, fwdState ? { state: fwdState } : undefined);
      return;
    }
    if (videoAsset?.liveProductionId) {
      navigate(`/production/${videoAsset.liveProductionId}?stage=review`);
    } else if (videoAsset?.aiProductionId) {
      navigate(`/ai?card=${videoAsset.aiProductionId}`);
    } else {
      navigate(-1);
    }
  }

  const [qualTransitioning, setQualTransitioning] = useState(false);
  async function handleQualTransition(newStatus) {
    if (qualTransitioning || !videoAsset) return;
    const endpoint = videoAsset.liveProductionId
      ? `/api/live-production/${videoAsset.liveProductionId}/status`
      : videoAsset.aiProductionId
        ? `/api/ai-production/${videoAsset.aiProductionId}/status`
        : null;
    if (!endpoint) return;
    setQualTransitioning(true);
    try {
      await api.patch(endpoint, { status: newStatus, changedBy: user?.id });
      // Video is no longer in Qual — return without reopening the modal
      if (location.state?.returnTo) {
        navigate(location.state.returnTo);
      } else {
        navigate(-1);
      }
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The status did not change. Try again.', { tone: 'danger' });
      setQualTransitioning(false);
    }
  }

  const [restoring, setRestoring] = useState(false);
  async function handleRestore() {
    const target = videoAsset?.liveProductionId ? 'Live production' : 'AI production';
    const ok = await confirm({
      title: 'Restore this version?',
      body: `This video becomes the current ${target} card video. If the card already has a current video, that one is archived.`,
      confirmLabel: 'Restore version',
    });
    if (!ok) return;
    setRestoring(true);
    try {
      await api.post(`/api/video-assets/${videoAssetId}/restore`);
      if (videoAsset?.liveProductionId) {
        navigate(`/production/${videoAsset.liveProductionId}?stage=review`);
      } else if (videoAsset?.aiProductionId) {
        navigate(`/ai?card=${videoAsset.aiProductionId}`);
      } else if (location.state?.returnTo) {
        navigate(location.state.returnTo);
      } else {
        navigate(-1);
      }
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The version was not restored. Try again.', { tone: 'danger' });
    } finally {
      setRestoring(false);
    }
  }

  async function handlePermanentDelete() {
    const ok = await confirm({
      title: 'Delete this archived video permanently?',
      body: `${clean(videoAsset.fileName)} and all its comments are deleted from storage. This cannot be undone.`,
      confirmLabel: 'Delete permanently',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/api/video-assets/${videoAssetId}/permanent`);
      // Go back to production or navigate to another archived version
      const others = archivedVersions.filter(v => v.id !== videoAssetId);
      if (others.length) {
        navigate(`/review/${others[0].id}`, { state: location.state });
      } else if (location.state?.returnTo) {
        navigate(location.state.returnTo);
      } else if (videoAsset?.liveProductionId) {
        navigate(`/production/${videoAsset.liveProductionId}?stage=review`);
      } else if (videoAsset?.aiProductionId) {
        navigate(`/ai?card=${videoAsset.aiProductionId}`);
      } else {
        navigate(-1);
      }
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The video was not deleted. Try again.', { tone: 'danger' });
    }
  }

  const markers = useMemo(() => comments.filter(c => !c.parentId).map(c => ({
    id: c.id, at: Number(c.timestampSeconds), label: `Comment by ${clean(c.commenter?.name || c.commenterName || 'Unknown')}`,
  })), [comments]);

  const backLabel = location.state?.returnLabel === QUAL_LABEL
    ? 'Close'
    : videoAsset?.liveProductionId ? 'Back to Live production'
    : videoAsset?.aiProductionId   ? 'Back to AI production'
    : 'Back';

  if (loading) {
    return (
      <div className="ui ui-review ui-review--fit" aria-busy="true">
        <BreadcrumbBar crumbs={['Review']} actions={<TierToggle />} />
        <Stage label="Loading video"><Skeleton variant="block" label="Loading video" /></Stage>
        <aside className="ui ui-comments" aria-label="Comments">
          <div className="ui-comments__list">{[0, 1, 2].map(i => <Skeleton key={i} variant="block" height="84px" />)}</div>
        </aside>
      </div>
    );
  }

  if (error || !videoAsset) {
    return (
      <div className="ui ui-review ui-review--fit ui-review--no-comments">
        <BreadcrumbBar onBack={() => navigate(-1)} crumbs={['Review']} actions={<TierToggle />} />
        <div className="ui-review-msg">
          {error === 'denied' && <EmptyState message="This video is not available to your account. Ask your Journey Viral contact if you need to review it." action={{ label: 'Go back', onClick: () => navigate(-1) }} />}
          {error === 'missing' && <EmptyState message="This video was not found. It may have been deleted, or the link is wrong." action={{ label: 'Go back', onClick: () => navigate(-1) }} />}
          {(error === 'failed' || !error) && <ErrorState title="The video did not load" message="Check your connection and try again." onRetry={() => setReload(n => n + 1)} />}
        </div>
      </div>
    );
  }

  const scriptName =
    videoAsset.liveProduction?.script?.scriptName ||
    videoAsset.aiProduction?.script?.scriptName ||
    videoAsset.liveProduction?.notes;

  const isArchived = !!videoAsset.isArchived;
  const qualContext = location.state?.returnLabel === QUAL_LABEL;
  const transcoding = videoAsset.transcodingStatus === 'pending' || videoAsset.transcodingStatus === 'processing';
  const versionLabel = isArchived ? `Archived ${fmtDate(videoAsset.archivedAt)}` : 'Current version';
  const otherVersions = archivedVersions.filter(v => v.id !== videoAssetId);
  const crumbs = [clean(videoAsset.client?.name), clean(scriptName), clean(videoAsset.fileName)].filter(Boolean);

  const actions = (
    <>
      {isArchived && <Badge tone="warn">Archived</Badge>}
      {archivedVersions.length > 0 && (
        <Popover open={versionsOpen} onOpenChange={setVersionsOpen} align="end" label="Versions"
          trigger={({ toggle, ref, props }) => (
            <Button ref={ref} variant="secondary" icon={Layers} onClick={toggle} {...props} aria-label={`Version: ${versionLabel}. Other versions`}>{versionLabel}</Button>
          )}>
          <Menu label="Other versions" onClose={() => setVersionsOpen(false)}
            items={otherVersions.length ? otherVersions.map(v => ({
              label: `Archived ${fmtDate(v.archivedAt)} (${v.commentCount} comment${v.commentCount !== 1 ? 's' : ''})`,
              onSelect: () => navigate(`/review/${v.id}`),
            })) : [{ label: 'No other versions', disabled: true }]} />
        </Popover>
      )}
      {isArchived && isAdmin && <Button variant="secondary" icon={ArchiveRestore} onClick={handleRestore} loading={restoring}>Restore version</Button>}
      {isArchived && isAdmin && canPermanentDelete && <Button variant="danger" icon={Trash2} onClick={handlePermanentDelete} disabled={restoring}>Delete permanently</Button>}
      {/* Qual-context actions: change the video's status without bouncing back to the Qual page */}
      {qualContext && (
        <>
          <Button variant="secondary" icon={Undo2} onClick={() => handleQualTransition('Editing')} disabled={qualTransitioning}>Back to editing</Button>
          <Button variant="primary" icon={ThumbsUp} onClick={() => handleQualTransition('Approved')} loading={qualTransitioning}>Approve</Button>
        </>
      )}
      <TierToggle />
    </>
  );

  const details = (
    <>
      <dl className="ui-kv">
        <dt>File</dt><dd>{clean(videoAsset.fileName)}</dd>
        {videoAsset.client?.name && <><dt>Client</dt><dd>{clean(videoAsset.client.name)}</dd></>}
        {scriptName && <><dt>Production</dt><dd>{clean(scriptName)}</dd></>}
        <dt>Version</dt><dd>{versionLabel}</dd>
        {videoAsset.createdAt && <><dt>Uploaded</dt><dd>{fmtDate(videoAsset.createdAt)}</dd></>}
        {fmtSize(videoAsset.fileSizeBytes) && <><dt>Size</dt><dd className="ui-mono">{fmtSize(videoAsset.fileSizeBytes)}</dd></>}
        {Number(videoAsset.durationSeconds) > 0 && <><dt>Length</dt><dd><Timecode seconds={Number(videoAsset.durationSeconds)} format={tcFormat} fps={30} /></dd></>}
      </dl>
      {transcription && <TranscriptSection transcription={transcription} baseName={videoAsset.fileName} onSeek={secs => handleSeekTo(secs)} />}
    </>
  );

  return (
    <div className={commentsHidden ? 'ui ui-review ui-review--fit ui-review--no-comments' : 'ui ui-review ui-review--fit'}>
      <BreadcrumbBar onBack={handleBack} backLabel={backLabel} crumbs={crumbs} actions={actions}
        onShare={canShare && !isArchived ? () => setShareOpen(true) : undefined}
        commentsHidden={commentsHidden} onToggleComments={() => setCommentsHidden(h => !h)} />
      {transcoding ? (
        <Stage label={clean(videoAsset.fileName)}>
          <div className="ui-review-msg">
            <h2 className="ui-heading">Preparing the video for playback</h2>
            <p className="ui-p ui-text-2">This file's codec does not play in browsers, so a compatible copy is being made in the background. Reload the page in a minute or two.</p>
          </div>
        </Stage>
      ) : (
        <ReviewStage
          key={videoAssetId}
          src={videoAsset.playbackUrl || videoAsset.cloudfrontUrl}
          poster={videoAsset.thumbnailUrl || undefined}
          label={clean(videoAsset.fileName)}
          comments={comments}
          markers={markers}
          activeMarker={activeCommentId}
          onMarker={m => setActiveCommentId(m.id)}
          seekRef={seekRef}
          onTimeUpdate={setCurrentTimeSeconds}
          onPlayerReady={() => setPlayerReady(true)}
          format={tcFormat}
          fps={30}
          playerRef={playerApi}
        />
      )}
      {!commentsHidden && (
        <CommentPanel
          key={videoAssetId}
          videoAssetId={videoAssetId}
          currentTimeSeconds={currentTimeSeconds}
          onSeekTo={handleSeekTo}
          comments={comments}
          onCommentsChange={fetchComments}
          activeCommentId={activeCommentId}
          canPost={!isArchived}
          identity={{ name: user?.name, initials: user?.initials }}
          slot={isArchived ? <div className="ui ui-composer"><p className="ui-composer__note">This is an archived version. Its comments are read-only.</p></div> : null}
          onTypingStart={() => playerApi.current?.pause()}
          details={details}
          format={tcFormat}
          fps={30}
          emptyMessage={isArchived ? 'This archived version has no comments.' : undefined}
        />
      )}
      {shareOpen && <ShareLinkManager videoAssetId={videoAssetId} open={shareOpen} onOpenChange={setShareOpen} />}
    </div>
  );
}
